import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { useQueryClient } from "@tanstack/react-query";
import type { Theme, ThemeFamily, ThemeStatus } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateAdminThemes } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type UpdateThemeInput = {
  themeId: string;
  family?: ThemeFamily;
  nameAr?: string;
  nameEn?: string;
  descriptionAr?: string;
  descriptionEn?: string;
  niches?: string[];
  sortWeight?: number;
  status?: ThemeStatus;
};

export function useUpdateThemeMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ themeId, ...body }: UpdateThemeInput) =>
      apiJsonClient<Theme>(endpoints.admin.themeById(themeId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: async (_data, variables) => {
      await invalidateAdminThemes(queryClient);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.admin.themeById(variables.themeId),
        exact: true,
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
